import CoinHeartbeat from "@components/utils/CoinHeartbeat";
import { randomInt } from "@utils/misc";
import { getThemeAsset } from "@utils/theme";
import { motion, Variants } from "framer-motion";
import { cn, PropsWithClassName } from "kz-ui-sdk";
import { AriaAttributes, useCallback, useEffect, useMemo, useState } from "react";

const ANIMATION_CONFIG = {
  DEFAULT_DURATION: 2.5,
  DEFAULT_REPEAT_DELAY: 0.8,
  INITIAL_DELAY: 1500,
  HEART_REAR_COUNT: 16,
  HEART_REAR_RADIUS_MIN: 50,
  HEART_REAR_RADIUS_MAX: 100,
  HEART_REAR_SIZE: 24,
  HEART_REAR_RANDOM_OFFSET_RANGE: 10,
  HEART_REAR_ROTATION_RANGE: 40,
  HEART_REAR_SCALE_MIN: 0.7,
  HEART_REAR_SCALE_MAX: 1.1,
} as const;

enum ANIMATION_STATE {
  IDLE = "idle",
  ANIMATING = "animating",
}

type AnimationElement =
  | "button"
  | "impulse"
  | "heartCenter"
  | "heartCenterLarge"
  | "border"
  | "coinPulse"
  | "shades"
  | "heartRear";

interface CSCompensationFABProps extends PropsWithClassName, AriaAttributes {
  animate?: {
    repeatDelay?: number;
    duration?: number;
  };
  zIndex?: number;
  containerClassName?: string;
  initialDelay?: number;
  onAnimationStart?: () => void;
  onClick?: () => void;
}

const CSCompensationFAB = ({
  className,
  ["aria-label"]: ariaLabel,
  animate,
  zIndex = 10,
  initialDelay = ANIMATION_CONFIG.INITIAL_DELAY,
  onAnimationStart,
  onClick,
}: CSCompensationFABProps) => {
  const {
    duration: animationDuration = ANIMATION_CONFIG.DEFAULT_DURATION,
    repeatDelay: animationRepeatDelay = ANIMATION_CONFIG.DEFAULT_REPEAT_DELAY,
  } = animate ?? {};
  const [animationState, setAnimationState] = useState<ANIMATION_STATE>(ANIMATION_STATE.IDLE);

  const heartPositions = useMemo(() => {
    return Array.from({ length: ANIMATION_CONFIG.HEART_REAR_COUNT }, (_, index) =>
      calculateHeartPosition(index, ANIMATION_CONFIG.HEART_REAR_COUNT),
    );
  }, []);

  // Auto start animations after initial delay
  // Make sure the animations start after the component is mounted
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationState(ANIMATION_STATE.ANIMATING);
      onAnimationStart?.();
    }, initialDelay);

    return () => clearTimeout(timer);
  }, [initialDelay, onAnimationStart]);

  const layerZIndexes = useMemo<Record<AnimationElement, number>>(() => {
    return {
      shades: zIndex - 2,
      heartRear: zIndex - 1,
      button: zIndex,
      impulse: zIndex + 1,
      heartCenter: zIndex + 2,
      heartCenterLarge: zIndex + 3,
      border: zIndex + 4,
      coinPulse: zIndex + 5,
    };
  }, [zIndex]);

  // Animation variants for all elements
  const animationVariants = useMemo(
    () =>
      getElementAnimationVariants({
        duration: animationDuration,
        repeatDelay: animationRepeatDelay,
      }),
    [animationDuration, animationRepeatDelay],
  );

  const handleClick = useCallback(() => {
    onClick?.();
  }, [onClick]);

  return (
    <div
      className={cn("relative h-[90px] w-[90px]", className)}
      style={{ zIndex }}
    >
      {/* Shades */}
      <div
        className={cn(
          "absolute left-1/2 top-1/2 z-[-1] h-[90px] w-[90px] -translate-x-1/2 -translate-y-1/2 rounded-full",
          "bg-[radial-gradient(50%_50%_at_50%_50%,_#0E082A_0%,rgba(14,8,42,0.00)_100%)]",
        )}
        style={{ zIndex: layerZIndexes.shades }}
      ></div>
      {/* Heart rear - decorative hearts around the component */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2"
        style={{ zIndex: layerZIndexes.heartRear }}
      >
        {heartPositions.map((position, index) => {
          const heartKey = `heart-rear-${index}`;

          return (
            <motion.img
              key={heartKey}
              src="/images/cs-compensation/heart-rear.png"
              alt={`Decorative heart ${index + 1}`}
              variants={getHeartRearVariants(position, animationDuration, animationRepeatDelay)}
              initial={ANIMATION_STATE.IDLE}
              animate={animationState}
              className={cn("absolute left-1/2 top-1/2 h-6 w-6")}
              style={{ zIndex: layerZIndexes.heartRear - 1 }}
            />
          );
        })}
      </div>
      {/* Button */}
      <motion.div
        onClick={handleClick}
        aria-label={ariaLabel}
        variants={animationVariants.button}
        initial={ANIMATION_STATE.IDLE}
        animate={animationState}
        className={cn(
          "absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full hover:cursor-pointer",
          "shadow-[0_1px_0_0_#29103A,0_8px_16px_0_#000]",
          "bg-[radial-gradient(45.41%_100%_at_50%_0%,_theme('colors.brand.500')_0%,theme('colors.accentBrand.800')_100%)]",
        )}
        style={{ zIndex: layerZIndexes.button }}
      >
        {/* Impulse */}
        <motion.div
          variants={animationVariants.impulse}
          initial={ANIMATION_STATE.IDLE}
          animate={animationState}
          className={cn(
            "pointer-events-none absolute left-1/2 top-1/2 z-[-1] -translate-x-1/2 -translate-y-1/2 rounded-full",
            "bg-[radial-gradient(45.41%_100%_at_50%_0%,_theme('colors.pink.500')_0%,theme('colors.pink.700')_100%)]",
          )}
          style={{ zIndex: layerZIndexes.impulse }}
        ></motion.div>

        {/* Heart center small */}
        <motion.img
          src="/images/cs-compensation/heart-center.png"
          alt="Heart center icon"
          variants={animationVariants.heartCenter}
          initial={ANIMATION_STATE.IDLE}
          animate={animationState}
          className={cn(
            "pointer-events-none absolute left-1/2 top-[50%] z-[-1] h-full w-full max-w-none -translate-x-1/2 -translate-y-1/2",
          )}
          style={{ zIndex: layerZIndexes.heartCenter }}
        />

        {/* Heart center large */}
        <motion.img
          src={getThemeAsset("/cs-compensation/heart-center-large.png")}
          alt="Large heart center decoration"
          variants={animationVariants.heartCenterLarge}
          initial={ANIMATION_STATE.IDLE}
          animate={animationState}
          className={cn("pointer-events-none absolute left-1/2 top-[42%] z-[-1] h-[90px] w-[90px] max-w-none")}
          style={{ zIndex: layerZIndexes.heartCenterLarge }}
        />
      </motion.div>
      {/* Border */}
      <motion.div
        variants={animationVariants.border}
        initial={ANIMATION_STATE.IDLE}
        animate={animationState}
        className={
          "pointer-events-none absolute left-1/2 top-1/2 h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 rounded-full shadow-[0_0_2px_rgba(255,255,255,0.25)]"
        }
        style={{ zIndex: layerZIndexes.border }}
      ></motion.div>

      {/* Coin Pulse */}
      <CoinHeartbeat className="absolute right-4 top-3.5 z-[41] h-4 w-4" />
    </div>
  );
};

const calculateHeartPosition = (index: number, totalHearts: number) => {
  const angle = (index * 360) / totalHearts;
  const radians = (angle * Math.PI) / 180;
  const radius = randomInt(ANIMATION_CONFIG.HEART_REAR_RADIUS_MIN, ANIMATION_CONFIG.HEART_REAR_RADIUS_MAX);

  const randomXOffset = randomInt(
    -ANIMATION_CONFIG.HEART_REAR_RANDOM_OFFSET_RANGE,
    ANIMATION_CONFIG.HEART_REAR_RANDOM_OFFSET_RANGE,
  );
  const randomYOffset = randomInt(
    -ANIMATION_CONFIG.HEART_REAR_RANDOM_OFFSET_RANGE,
    ANIMATION_CONFIG.HEART_REAR_RANDOM_OFFSET_RANGE,
  );

  const targetX = Math.round(Math.cos(radians) * radius) + randomXOffset;
  const targetY = Math.round(Math.sin(radians) * radius) + randomYOffset;

  return {
    targetX,
    targetY,
    startX: Math.round(targetX * 0.2),
    startY: Math.round(targetY * 0.2),
    middleX: Math.round(targetX * 0.9),
    middleY: Math.round(targetY * 0.9),
    randomRotation:
      Math.random() * ANIMATION_CONFIG.HEART_REAR_ROTATION_RANGE - ANIMATION_CONFIG.HEART_REAR_ROTATION_RANGE / 2,
    randomScale:
      Math.random() * (ANIMATION_CONFIG.HEART_REAR_SCALE_MAX - ANIMATION_CONFIG.HEART_REAR_SCALE_MIN) +
      ANIMATION_CONFIG.HEART_REAR_SCALE_MIN,
  };
};

const getElementAnimationVariants = ({
  duration,
  repeatDelay,
}: {
  duration: number;
  repeatDelay: number;
}): Partial<Record<AnimationElement, Variants>> => ({
  button: {
    [ANIMATION_STATE.IDLE]: {
      width: 64,
      height: 64,
      transformOrigin: "center",
    },
    [ANIMATION_STATE.ANIMATING]: {
      width: [64, 60, 60, 64],
      height: [64, 60, 60, 64],
      transformOrigin: "center",
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.1, 0.5, 1],
        repeat: Infinity,
      },
    },
  },
  impulse: {
    [ANIMATION_STATE.IDLE]: {
      width: 0,
      height: 0,
      transformOrigin: "center",
    },
    [ANIMATION_STATE.ANIMATING]: {
      width: [0, 60, 60, 0],
      height: [0, 60, 60, 0],
      transformOrigin: "center",
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.1, 0.5, 1],
        repeat: Infinity,
        ease: [[0.25, 0.46, 0.45, 0.94], "linear", "easeOut", "linear"],
      },
    },
  },
  heartCenter: {
    [ANIMATION_STATE.IDLE]: {
      scale: 1,
      translateX: "-50%",
      translateY: "-50%",
    },
    [ANIMATION_STATE.ANIMATING]: {
      scale: [1, 1, 0.9, 1, 0.9, 1],
      translateX: "-50%",
      translateY: "-50%",
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.05, 0.2, 0.25, 0.4, 1],
        repeat: Infinity,
      },
    },
  },
  heartCenterLarge: {
    [ANIMATION_STATE.IDLE]: {
      opacity: 1,
      scale: 1,
      translateX: "-50%",
      translateY: "-50%",
      transformOrigin: "center",
    },
    [ANIMATION_STATE.ANIMATING]: {
      opacity: [1, 0, 0, 1],
      scale: [1, 0.8, 0.8, 1],
      translateX: "-50%",
      translateY: "-50%",
      transformOrigin: "center",
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.1, 0.5, 1],
        repeat: Infinity,
        ease: [[0.25, 0.46, 0.45, 0.94], "linear", "linear", "linear"],
      },
    },
  },
  border: {
    [ANIMATION_STATE.IDLE]: {
      width: 60,
      height: 60,
      transformOrigin: "center",
    },
    [ANIMATION_STATE.ANIMATING]: {
      width: [60, 56, 56, 60],
      height: [60, 56, 56, 60],
      transformOrigin: "center",
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.1, 0.5, 1],
        repeat: Infinity,
      },
    },
  },
});

const getHeartRearVariants = (
  position: ReturnType<typeof calculateHeartPosition>,
  duration: number,
  repeatDelay: number,
): Variants => {
  const halfSize = ANIMATION_CONFIG.HEART_REAR_SIZE / 2;
  return {
    [ANIMATION_STATE.IDLE]: {
      scale: position.randomScale,
      rotate: position.randomRotation,
      opacity: 0,
      x: position.startX - halfSize,
      y: position.startY - halfSize,
    },
    [ANIMATION_STATE.ANIMATING]: {
      scale: position.randomScale,
      x: [position.startX - halfSize, position.middleX - halfSize, position.targetX - halfSize],
      y: [position.startY - halfSize, position.middleY - halfSize, position.targetY - halfSize],
      rotate: position.randomRotation,
      opacity: [1, 1, 0, 0, 0],
      transition: {
        duration,
        repeatDelay,
        times: [0, 0.12, 0.7, 1],
        repeat: Infinity,
        ease: ["easeOut", "linear", "linear", "linear"], // smooth drift, clean fade
      },
    },
  };
};

export default CSCompensationFAB;
